import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "@/hooks/use-toast";
import { api } from "@/lib/api";
import { useQueryClient } from "@tanstack/react-query";
import {
  Activity,
  CheckCircle2,
  Circle,
  Clock,
  Eye,
  FileSignature,
  FileText,
  Heart,
  Scissors,
  Stethoscope,
  TestTube
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { AssinarDocumentoButton } from "./components/AssinarDocumentoButton";
import { AssinaturaStatusBadge } from "./components/AssinaturaStatusBadge";
import { ExamesEDocumentos } from "./components/ExamesEDocumentos";
import { Header } from "./components/header/header";
import { HistoricoDocumentosAssinadosModal } from "./components/HistoricoDocumentosAssinadosModal";
import { TimelineStep } from "./components/linhaDoTempo";
import { getTipoProtocolo } from "./constants/tipoProtocolo";
import { FORM_STATUS_MAP } from "./constants/statusAtendimento";
import {
  AvaliacaoCardiologica,
  ConsultaOftalmologica,
  EncaminhamentoCirurgico,
  IdentificacaoPaciente,
  ProntuarioGeralista,
  ProntuarioRetinologo,
  Triagem
} from "./forms";
import AnaminesePreOperatoria from "./forms/anaminesePreOperatoria";
import { DocumentoAssinaturaCell } from "./components/DocumentoAssinaturaCell";
import { Document, DocumentStatus, StatusAtendimento } from "./interfaces/atendimento.interface";



// Função auxiliar para mapear nome do ícone para componente do lucide-react
const getIconComponent = (iconName?: string) => {
  const iconMap: Record<string, any> = {
    FileText,
    Activity,
    Stethoscope,
    Eye,
    Heart,
    TestTube,
    FileSignature,
    Scissors
  };
  return iconMap[iconName || ''] || FileText;
};

// Função para mapear status do backend para o status do frontend
const mapStatus = (status: string): "completed" | "pending" | "not_started" => {
  switch (status) {
    case 'CONCLUIDO':
      return 'completed';
    case 'EM_ANDAMENTO':
      return 'pending';
    case 'PENDENTE':
    case 'CANCELADO':
    default:
      return 'not_started';
  }
};

const AtendimentoForm = () => {
  const navigate = useNavigate();
  const { patientId } = useParams();
  const location = useLocation();
  const queryClient = useQueryClient();

  // Dados completos do atendimento
  const [atendimento, setAtendimento] = useState<any>(null);
  const [patient, setPatient] = useState<any>(null);
  const [pacienteId, setPacienteId] = useState<number | undefined>(undefined);
  const [empresaId, setEmpresaId] = useState<number | undefined>(undefined);

  const [doctor, setDoctor] = useState({
    id: 1,
    name: "Dr. Carlos Silva",
    specialty: "Oftalmologia Geral"
  });

  // Função para carregar linha do tempo do atendimento
  // const loadLinhaTempo = async (atendimentoId: number) => {
  //   try {
  //     const response = await api.get(`/api/atendimento_linha_tempo/atendimento/${atendimentoId}`);
  //     const linhasTempo = response.result || [];
      
  //     // Mapear dados do backend para TimelineStep
  //     const steps: TimelineStep[] = linhasTempo.map((linha: any) => ({
  //       id: linha.id.toString(),
  //       title: linha.etapa?.nome || 'Etapa',
  //       status: mapStatus(linha.status_atendimento),
  //       icon: getIconComponent(linha.etapa?.icone),
  //       doctor: linha.atendimento?.medico?.pessoas?.nome || undefined,
  //       date: linha.data_inicio 
  //         ? new Date(linha.data_inicio).toLocaleDateString('pt-BR')
  //         : undefined
  //     }));
      
  //     setTimelineSteps(steps);
  //   } catch (error) {
  //     console.error('Erro ao carregar linha do tempo:', error);
  //     toast({
  //       title: "Erro",
  //       description: "Não foi possível carregar a linha do tempo do atendimento.",
  //       variant: "destructive"
  //     });
  //   }
  // };

  // Função para buscar dados completos do atendimento
  const loadatendimento = async (atendimentoId: number) => {
    try {
      const response = await api.get(`/api/atendimentos/${atendimentoId}`);
      if (response.result) {
        const atendimentoCompleto = response.result;
        setAtendimento(atendimentoCompleto);
        
        // Atualizar status do atendimento
        if (atendimentoCompleto.status_atendimento) {
          setCurrentStatus(atendimentoCompleto.status_atendimento as StatusAtendimento);
        }
        
        // Extrair id_empresa do atendimento
        if (atendimentoCompleto.id_empresa) {
          setEmpresaId(atendimentoCompleto.id_empresa);
        }
        
        // Extrair dados do paciente do atendimento
        if (atendimentoCompleto.paciente) {
          const paciente = atendimentoCompleto.paciente;
          const pessoa = paciente.pessoa;
          const pessoaFisica = pessoa?.pessoaFisica;
          
          // Armazenar id do paciente
          setPacienteId(paciente.id);
          
          // Buscar data de nascimento em diferentes locais possíveis
          const dataNascimento = pessoaFisica?.data_nascimento || pessoa?.data_nascimento || "";
          
          // Calcular idade se tiver data de nascimento
          let age = 0;
          if (dataNascimento) {
            const nascimento = new Date(dataNascimento);
            if (!isNaN(nascimento.getTime())) {
              const hoje = new Date();
              age = hoje.getFullYear() - nascimento.getFullYear();
              const mes = hoje.getMonth() - nascimento.getMonth();
              if (mes < 0 || (mes === 0 && hoje.getDate() < nascimento.getDate())) {
                age--;
              }
            }
          }
          
          setPatient({
            id: paciente.id,
            name: pessoa?.nome || '',
            cpf: pessoaFisica?.cpf || '',
            age: age,
            dataNascimento: dataNascimento,
            insurance: paciente.convenio || 'Particular'
          });
        }
        
        // Extrair dados do médico do atendimento
        if (atendimentoCompleto.medico) {
          const medico = atendimentoCompleto.medico;
          const medicoPessoa = medico.pessoa;
          
          setDoctor({
            id: medico.id,
            name: medicoPessoa?.nome || 'Médico',
            specialty: atendimentoCompleto.tipoAtendimento?.nome || 'Oftalmologia Geral'
          });
        }
        
        // Extrair dados dos protocolos se existirem
        if (atendimentoCompleto.protocolosTriagem && atendimentoCompleto.protocolosTriagem.length > 0) {
          const triagem = atendimentoCompleto.protocolosTriagem[0];
          setTriagemData(triagem);
          setTriagemId(triagem.id);
        }
        
        if (atendimentoCompleto.protocolosIdentificacaoPaciente && atendimentoCompleto.protocolosIdentificacaoPaciente.length > 0) {
          const identificacao = atendimentoCompleto.protocolosIdentificacaoPaciente[0];
          setIdentificacaoPacienteData(identificacao);
        }
        
        if (atendimentoCompleto.protocolosConsultaOftalmologica && atendimentoCompleto.protocolosConsultaOftalmologica.length > 0) {
          const consulta = atendimentoCompleto.protocolosConsultaOftalmologica[0];
          setConsultaOftalmologicaData(consulta);
        }
        
        if (atendimentoCompleto.protocolosProntuarioRetinologo && atendimentoCompleto.protocolosProntuarioRetinologo.length > 0) {
          const retinologo = atendimentoCompleto.protocolosProntuarioRetinologo[0];
          setProntuarioRetinologoData(retinologo);
        }
        
        if (atendimentoCompleto.protocolosAnamnesePreOperatoria && atendimentoCompleto.protocolosAnamnesePreOperatoria.length > 0) {
          const anamnese = atendimentoCompleto.protocolosAnamnesePreOperatoria[0];
          setAnaminesePreOperatoriaData(anamnese);
        }
        
        if (atendimentoCompleto.protocolosAvaliacaoCardiologica && atendimentoCompleto.protocolosAvaliacaoCardiologica.length > 0) {
          const avaliacao = atendimentoCompleto.protocolosAvaliacaoCardiologica[0];
          setAvaliacaoCardiologicaData(avaliacao);
        }
        
        if (atendimentoCompleto.protocolosProntuarioGeralista && atendimentoCompleto.protocolosProntuarioGeralista.length > 0) {
          const geralista = atendimentoCompleto.protocolosProntuarioGeralista[0];
          setProntuarioGeralistaData(geralista);
        }
        
        if (atendimentoCompleto.protocolosEncaminhamentoCirurgico && atendimentoCompleto.protocolosEncaminhamentoCirurgico.length > 0) {
          const encaminhamento = atendimentoCompleto.protocolosEncaminhamentoCirurgico[0];
          setEncaminhamentoCirurgicoData(encaminhamento);
        }
        
        // Atualizar observações do atendimento
        if (atendimentoCompleto.observacoes) {
          setObservations(atendimentoCompleto.observacoes);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar dados completos do atendimento:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar os dados do atendimento.",
        variant: "destructive"
      });
    }
  };

  // Atualizar dados do atendimento quando vierem do modal ou pela URL
  useEffect(() => {
    let atendimentoId: number | null = null;
    
    // Priorizar atendimento do estado da navegação
    if (location.state?.atendimento) {
      atendimentoId = location.state.atendimento.id || location.state.atendimento;
    } else if (patientId) {
      // Se não tiver no estado, usar o patientId da URL (que na verdade é o id do atendimento)
      atendimentoId = Number(patientId);
    }
    
    // Se tiver atendimentoId, carregar dados completos do atendimento
    if (atendimentoId) {
      loadatendimento(atendimentoId);
      // loadLinhaTempo(atendimentoId);
      loadCurrentStatus(atendimentoId);
    }
    
    // Se vier dados do paciente e médico do estado (fallback), usar temporariamente até carregar do backend
    if (location.state?.patient) {
      setPatient(location.state.patient);
      if (location.state.patient.id) {
        setPacienteId(location.state.patient.id);
      }
    }
    if (location.state?.doctor) {
      setDoctor(location.state.doctor);
    }
    if (location.state?.atendimento?.id_empresa) {
      setEmpresaId(location.state.atendimento.id_empresa);
    }
  }, [location.state?.atendimento?.id, location.state?.atendimento, patientId]);


  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [documents, setDocuments] = useState<Document[]>([
    { id: "1", name: "Identificação do Paciente", category: "Recepção", status: "completed" },
    { id: "2", name: "Triagem", category: "Enfermagem", status: "completed" },
    { id: "3", name: "Prontuário Geralista", category: "Médico", status: "pending", fields: {} },
    { id: "4", name: "Consulta Oftalmológica", category: "Oftalmologia", status: "pending", fields: {} },
    { id: "5", name: "Prontuário Retinólogo", category: "Retinologia", status: "not_started" },
    { id: "6", name: "Avaliação Cardiológica (Escore Lee)", category: "Cardiologia", status: "not_started" },
    { id: "7", name: "Anamnese Pré-Operatória", category: "Cirurgia", status: "not_started" },
    { id: "8", name: "Encaminhamento Cirúrgico", category: "Cirurgia", status: "not_started" },
    // { id: "9", name: "Pós-Cirúrgico", category: "Cirurgia", status: "not_started" },
  ]);

  const [timelineSteps, setTimelineSteps] = useState<TimelineStep[]>([
    { id: "1", title: "Identificação", status: "completed", icon: FileText, doctor: "Recepção", date: "06/10/2025" },
    { id: "2", title: "Enfermagem", status: "completed", icon: Activity, doctor: "Enf. Maria", date: "06/10/2025" },
    { id: "3", title: "Consulta Geralista", status: "pending", icon: Stethoscope },
    { id: "4", title: "Oftalmologista", status: "pending", icon: Eye },
    { id: "5", title: "Cardiologista", status: "not_started", icon: Heart },
    { id: "6", title: "Exames", status: "not_started", icon: TestTube },
    { id: "7", title: "Encaminhamento", status: "not_started", icon: FileSignature },
    { id: "8", title: "Cirurgia", status: "not_started", icon: Scissors },
  ]);

  const [formData, setFormData] = useState<Record<string, string>>({});
  const [observations, setObservations] = useState("");
  const [triagemData, setTriagemData] = useState<any>(null);
  const [triagemId, setTriagemId] = useState<number | null>(null);
  const [identificacaoPacienteData, setIdentificacaoPacienteData] = useState<any>(null);
  const [consultaOftalmologicaData, setConsultaOftalmologicaData] = useState<any>(null);
  const [prontuarioRetinologoData, setProntuarioRetinologoData] = useState<any>(null);
  const [anaminesePreOperatoriaData, setAnaminesePreOperatoriaData] = useState<any>(null);
  const [avaliacaoCardiologicaData, setAvaliacaoCardiologicaData] = useState<any>(null);
  const [prontuarioGeralistaData, setProntuarioGeralistaData] = useState<any>(null);
  const [encaminhamentoCirurgicoData, setEncaminhamentoCirurgicoData] = useState<any>(null);
  const [currentStatus, setCurrentStatus] = useState<StatusAtendimento | null>(null);
  
  // Refs para armazenar dados dos formulários e salvar ao fechar o modal
  const formDataRefs = useRef<Record<string, any>>({});
  const atendimentoId = useMemo(() => {
    return atendimento?.id || location.state?.atendimento?.id || (patientId ? Number(patientId) : undefined);
  }, [atendimento?.id, location.state?.atendimento?.id, patientId]);

  // Função para carregar o status atual do atendimento (status do atendimento, não do protocolo)
  const loadCurrentStatus = async (atendimentoId: number) => {
    try {
      const response = await api.get(`/api/atendimentos/${atendimentoId}`);
      if (response.result?.status_atendimento) {
        setCurrentStatus(response.result.status_atendimento as StatusAtendimento);
      }
    } catch (error) {
      console.error('Erro ao carregar status do atendimento:', error);
    }
  };

  // Função para lidar com mudança de status do header
  const handleStatusChange = async (newStatus: StatusAtendimento) => {
    setCurrentStatus(newStatus);
    // Recarregar dados do atendimento para garantir sincronização
    if (atendimentoId) {
      await loadatendimento(atendimentoId);
    }
  };

  // Escutar eventos de mudança de status
  useEffect(() => {
    const handleStatusChange = async (event: CustomEvent) => {
      const { status, atendimentoId: changedAtendimentoId } = event.detail;
      if (changedAtendimentoId === atendimentoId) {
        setCurrentStatus(status);
      }
    };

    window.addEventListener('statusAtendimentoChanged', handleStatusChange as EventListener);
    return () => {
      window.removeEventListener('statusAtendimentoChanged', handleStatusChange as EventListener);
    };
  }, [atendimentoId]);

  const getStatusColor = (status: DocumentStatus) => {
    switch (status) {
      case "completed": return "bg-success/10 text-success hover:bg-success/20";
      case "pending": return "bg-warning/10 text-warning hover:bg-warning/20";
      case "not_started": return "bg-muted text-muted-foreground hover:bg-muted/80";
    }
  };

  // Função para obter o status_atendimento de cada formulário específico
  const getFormStatus = (documentName: string): string | null => {
    switch (documentName) {
      case "Identificação do Paciente":
        return identificacaoPacienteData?.status_atendimento || null;
      case "Triagem":
        return triagemData?.status_atendimento || null;
      case "Prontuário Geralista":
        return prontuarioGeralistaData?.status_atendimento || null;
      case "Consulta Oftalmológica":
        return consultaOftalmologicaData?.status_atendimento || null;
      case "Prontuário Retinólogo":
        return prontuarioRetinologoData?.status_atendimento || null;
      case "Avaliação Cardiológica (Escore Lee)":
        return avaliacaoCardiologicaData?.status_atendimento || null;
      case "Anamnese Pré-Operatória":
        return anaminesePreOperatoriaData?.status_atendimento || null;
      case "Encaminhamento Cirúrgico":
        return encaminhamentoCirurgicoData?.status_atendimento || null;
      default:
        return null;
    }
  };

  // Protocolos que têm PDF e podem ser assinados (exclui identificacao_paciente)
  const TIPOS_COM_PDF = [
    'triagem',
    'consulta_oftalmologica',
    'prontuario_geralista',
    'prontuario_retinologo',
    'avaliacao_cardiologica',
    'anamnese_pre_operatoria',
    'encaminhamento_cirurgico'
  ]

  const getProtocoloId = (documentName: string): number | undefined => {
    switch (documentName) {
      case 'Identificação do Paciente':
        return identificacaoPacienteData?.id
      case 'Triagem':
        return triagemId ?? triagemData?.id
      case 'Prontuário Geralista':
        return prontuarioGeralistaData?.id
      case 'Consulta Oftalmológica':
        return consultaOftalmologicaData?.id
      case 'Prontuário Retinólogo':
        return prontuarioRetinologoData?.id
      case 'Avaliação Cardiológica (Escore Lee)':
        return avaliacaoCardiologicaData?.id
      case 'Anamnese Pré-Operatória':
        return anaminesePreOperatoriaData?.id
      case 'Encaminhamento Cirúrgico':
        return encaminhamentoCirurgicoData?.id
      default:
        return undefined
    }
  }

  const [historicoAssinaturasOpen, setHistoricoAssinaturasOpen] = useState(false)

  const getStatusIcon = (statusAtendimento?: string | null) => {
    if (!statusAtendimento || statusAtendimento === 'Novo') {
      return <Circle className="h-5 w-5 text-muted-foreground" />;
    }
    
    const statusUpper = statusAtendimento.toUpperCase();
    
    if (statusUpper.includes('CONCLUIDO') || statusUpper.includes('CONCLUÍDA')) {
      return <CheckCircle2 className="h-5 w-5 text-success" />;
    }
    
    if (statusUpper.includes('EM ANDAMENTO') || statusUpper.includes('EM CONSULTA')) {
      return <Clock className="h-5 w-5 text-warning" />;
    }
    
    // Default para status não reconhecido
    return <Circle className="h-5 w-5 text-muted-foreground" />;
  };

  // Função para verificar se um documento deve mostrar "Preencher" ou "Abrir"
  const shouldShowAbrir = (documentName: string): boolean => {
    // Identificação do Paciente sempre mostra "Abrir"
    if (documentName === "Identificação do Paciente") {
      return !!identificacaoPacienteData;
    }
    
    // Verificar se o protocolo já foi criado
    switch (documentName) {
      case "Triagem":
        return !!triagemData;
      case "Prontuário Geralista":
        return !!prontuarioGeralistaData;
      case "Consulta Oftalmológica":
        return !!consultaOftalmologicaData;
      case "Prontuário Retinólogo":
        return !!prontuarioRetinologoData;
      case "Avaliação Cardiológica (Escore Lee)":
        return !!avaliacaoCardiologicaData;
      case "Anamnese Pré-Operatória":
        return !!anaminesePreOperatoriaData;
      case "Encaminhamento Cirúrgico":
        return !!encaminhamentoCirurgicoData;
      default:
        return false;
    }
  };

  const completedDocs = documents.filter(d => d.status === "completed").length;
  const progress = (completedDocs / documents.length) * 100;

  const riskScore = "Baixo"; // Mock - seria calculado

  // Função para salvar observações usando a API
  const handleSaveObservations = async () => {
    try {
      const atendimentoId = Number(patientId);
      if (!atendimentoId) {
        toast({
          title: "Erro",
          description: "ID do atendimento não encontrado.",
          variant: "destructive"
        });
        return;
      }

      await api.put(`/api/atendimentos/${atendimentoId}`, {
        observacoes: observations
      });

      toast({
        title: "Observações salvas",
        description: "As observações foram salvas com sucesso!",
      });
    } catch (error) {
      console.error('Erro ao salvar observações:', error);
      toast({
        title: "Erro",
        description: "Não foi possível salvar as observações.",
        variant: "destructive"
      });
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header Fixo */}
        <Header 
          patient={patient}
          doctor={doctor}
          riskScore={riskScore}
          onBack={() => {
            // Invalidar cache dos atendimentos para forçar recarregamento
            queryClient.invalidateQueries({ queryKey: ['atendimentos'] });
            navigate("/atendimentos");
          }}
          status={currentStatus}
          atendimentoId={atendimentoId}
          onStatusChange={handleStatusChange}
        />

        {/* Timeline Interativa */}
        {/* <LinhaDoTempo 
          timelineSteps={timelineSteps}
          onStepClick={(step) => {
            const doc = documents.find(d => d.name.includes(step.title));
            if (doc) setSelectedDocument(doc);
          }}
        /> */}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Painel Principal */}
          <div className="space-y-6">
            {/* Tabela de Protocolos */}
            <Card className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Protocolos e Documentos</h3>
                {atendimentoId && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setHistoricoAssinaturasOpen(true)}
                  >
                    <FileSignature className="h-4 w-4 mr-2" />
                    Ver histórico de assinaturas
                  </Button>
                )}
              </div>
              <div className="space-y-2">
                {documents.map((doc) => {
                  const protocoloId = getProtocoloId(doc.name)
                  const tipoProtocolo = getTipoProtocolo(doc.name)
                  const mostraAssinatura =
                    tipoProtocolo &&
                    TIPOS_COM_PDF.includes(tipoProtocolo) &&
                    protocoloId &&
                    atendimentoId
                  return (
                    <div
                      key={doc.id}
                      className="flex items-center justify-between p-3 border rounded-lg hover:bg-accent/50 transition-colors"
                    >
                      <div className="flex items-center gap-3 flex-1">
                        {getStatusIcon(getFormStatus(doc.name))}
                        <div className="flex-1">
                          <p className="font-medium">{doc.name}</p>
                          <p className="text-sm text-muted-foreground">{doc.category}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {mostraAssinatura && (
                          <DocumentoAssinaturaCell
                            prontuarioId={atendimentoId!}
                            protocoloId={protocoloId}
                            tipoProtocolo={tipoProtocolo}
                          />
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelectedDocument(doc)}
                        >
                          {shouldShowAbrir(doc.name) ? (
                            <>
                              <Eye className="h-4 w-4 mr-2" />
                              Abrir
                            </>
                          ) : (
                            <>
                              <FileText className="h-4 w-4 mr-2" />
                              Preencher
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  )
                })}
              </div>
            </Card>
            {atendimentoId && empresaId != null && (
              <HistoricoDocumentosAssinadosModal
                open={historicoAssinaturasOpen}
                onOpenChange={setHistoricoAssinaturasOpen}
                prontuarioId={atendimentoId}
                tenantRef={String(empresaId)}
              />
            )}
          </div>

          {/* Card de Exames e Documentos */}
          <div>
            <ExamesEDocumentos atendimentoId={atendimentoId} />
          </div>
        </div>
      </div>

      {/* Modal de Documento */}
      <Dialog 
        open={!!selectedDocument} 
        onOpenChange={async (open) => {
          if (!open && selectedDocument) {
            // Salvar dados antes de fechar
            const currentFormData = formDataRefs.current[selectedDocument.id];
            if (currentFormData && atendimentoId) {
              // Atualizar status quando o formulário for fechado com dados
              const statusMap = FORM_STATUS_MAP[selectedDocument.name];
              if (statusMap?.completion) {
                try {
                  // Recarregar status atualizado
                  await loadCurrentStatus(atendimentoId);
                } catch (error) {
                  console.error('Erro ao atualizar status:', error);
                }
              }
              // Os formulários já salvam automaticamente, apenas limpa a ref
              formDataRefs.current[selectedDocument.id] = null;
            }
            setSelectedDocument(null);
            // Recarregar status ao fechar o modal
            if (atendimentoId) {
              await loadCurrentStatus(atendimentoId);
            }
          }
        }}
      >
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedDocument?.name}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {selectedDocument?.name === "Identificação do Paciente" && (
                <IdentificacaoPaciente 
                  onDataChange={(data) => {
                    // Só atualiza se os dados realmente mudaram para evitar loops
                    const currentDataString = JSON.stringify(identificacaoPacienteData);
                    const newDataString = JSON.stringify(data);
                    if (currentDataString !== newDataString) {
                      setIdentificacaoPacienteData(data);
                      if (selectedDocument) {
                        formDataRefs.current[selectedDocument.id] = data;
                      }
                    }
                  }}
                  initialData={atendimento}
                  atendimentoId={atendimentoId}
                  pacienteId={pacienteId}
                  empresaId={empresaId}
                />
              )}
          </div>
          <div className="space-y-4">
              {selectedDocument?.name === "Prontuário Geralista" && (
                <ProntuarioGeralista 
                  onDataChange={(data) => {
                    setProntuarioGeralistaData(data);
                    if (selectedDocument) {
                      formDataRefs.current[selectedDocument.id] = data;
                    }
                  }}
                  initialData={prontuarioGeralistaData}
                  atendimentoId={atendimentoId}
                  pacienteId={pacienteId}
                  empresaId={empresaId}
                />
              )}

              {selectedDocument?.name === "Prontuário Retinólogo" && (
                <ProntuarioRetinologo 
                  onDataChange={(data) => {
                    setProntuarioRetinologoData(data);
                    if (selectedDocument) {
                      formDataRefs.current[selectedDocument.id] = data;
                    }
                  }}
                  initialData={prontuarioRetinologoData}
                  atendimentoId={atendimentoId}
                  pacienteId={pacienteId}
                  empresaId={empresaId}
                />
              )}

              {selectedDocument?.name === "Anamnese Pré-Operatória" && (
                <AnaminesePreOperatoria 
                  onDataChange={(data) => {
                    setAnaminesePreOperatoriaData(data);
                    if (selectedDocument) {
                      formDataRefs.current[selectedDocument.id] = data;
                    }
                  }}
                  initialData={anaminesePreOperatoriaData}
                  atendimentoId={atendimentoId}
                  pacienteId={pacienteId}
                  empresaId={empresaId}
                />
              )}

              {selectedDocument?.name === "Consulta Oftalmológica" && (
                <ConsultaOftalmologica 
                  onDataChange={(data) => {
                    setConsultaOftalmologicaData(data);
                    if (selectedDocument) {
                      formDataRefs.current[selectedDocument.id] = data;
                    }
                  }}
                  initialData={consultaOftalmologicaData}
                  atendimentoId={atendimentoId}
                  pacienteId={pacienteId}
                  empresaId={empresaId}
                />
              )}

              {selectedDocument?.name === "Avaliação Cardiológica (Escore Lee)" && (
                <AvaliacaoCardiologica 
                  onDataChange={(data) => {
                    setAvaliacaoCardiologicaData(data);
                    if (selectedDocument) {
                      formDataRefs.current[selectedDocument.id] = data;
                    }
                  }}
                  initialData={avaliacaoCardiologicaData}
                  atendimentoId={atendimentoId}
                  pacienteId={pacienteId}
                  empresaId={empresaId}
                />
              )}

              {selectedDocument?.name === "Triagem" && (
                <Triagem 
                  onDataChange={(data) => {
                    setTriagemData(data);
                    if (selectedDocument) {
                      formDataRefs.current[selectedDocument.id] = data;
                    }
                  }}
                  initialData={triagemData}
                  atendimentoId={atendimentoId}
                  pacienteId={pacienteId}
                  empresaId={empresaId}
                />
              )}

              {selectedDocument?.name === "Encaminhamento Cirúrgico" && (
                <EncaminhamentoCirurgico 
                  onDataChange={(data) => {
                    setEncaminhamentoCirurgicoData(data);
                    if (selectedDocument) {
                      formDataRefs.current[selectedDocument.id] = data;
                    }
                  }}
                  initialData={encaminhamentoCirurgicoData}
                  atendimentoId={atendimentoId}
                  pacienteId={pacienteId}
                  empresaId={empresaId}
                />
              )}

              {/* {selectedDocument?.name === "Pós-Cirúrgico" && (
                <PosCirurgico 
                  onDataChange={(data) => {
                    // Aqui você pode salvar os dados pós-cirúrgicos
                    console.log('Dados pós-cirúrgicos:', data);
                  }}
                />
              )} */}
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default AtendimentoForm;
